/**
 * The liveness rule of a flow instance's public link (`/p/<publicToken>`),
 * shared by the API (`public-link-rules.ts` wraps it for every share, invite
 * and session check) and by the advisor invite modal, so both sides agree on
 * which links still work.
 *
 * Pure on purpose, so it is unit-tested hermetically in the CI batch.
 */

import { parseDbTimestamp } from "../db-timestamp";

type PublicLinkExpiryFields<R extends string> = {
  publicTokenExpiry: string | null | undefined;
  publicAccessRevokedReason?: R | null;
};

/**
 * Why a flow-instance-level public link no longer works, from the row alone:
 * an explicit `publicAccessRevokedReason` wins (set by
 * `revokeFlowInstancePublicToken` — the advisor revoked it, or a
 * representative change withdrew it automatically), otherwise a missing or
 * past `publicTokenExpiry` reads as `"expired"`. Null means the link is still
 * good.
 *
 * `publicTokenExpiry` is a `timestamp(mode: "string")` column: Postgres hands
 * it back as `"2026-09-30 10:00:00"`, which must never be compared lexically
 * with an ISO `"2026-09-30T…Z"` string (the space sorts before `T`, so every
 * link read as expired from 00:00 UTC on its expiry day). `parseDbTimestamp`
 * gives real instants; the exact expiry instant is still valid. `now` is
 * overridable for tests.
 */
export function publicLinkUnavailableReason<R extends string = never>(
  fi: PublicLinkExpiryFields<R>,
  now: number = Date.now(),
): R | "expired" | null {
  if (fi.publicAccessRevokedReason) return fi.publicAccessRevokedReason;
  if (!fi.publicTokenExpiry) return "expired";
  const expiresAt = parseDbTimestamp(fi.publicTokenExpiry);
  if (expiresAt !== null && expiresAt.getTime() < now) return "expired";
  return null;
}

/**
 * THE single "is this public link live right now" check.
 *
 * A revoke deliberately KEEPS `publicToken` set (so a withdrawn link can still
 * be looked up to show a real reason) and only nulls `publicTokenExpiry` — so
 * "does a token exist" is never enough on its own, and a NULL expiry must read
 * as "not live", never as "no expiry limit". Live requires all three: a
 * token, no revoke reason, and an expiry that is actually in the future.
 */
export function isPublicLinkLive<R extends string = never>(
  fi: PublicLinkExpiryFields<R> & { publicToken: string | null | undefined },
  now: number = Date.now(),
): boolean {
  return !!fi.publicToken && publicLinkUnavailableReason(fi, now) === null;
}
